import { AnnouncementsViewProps } from "./AnnouncementsView.types";
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { useBeforeRender } from "../../../utils/utilFunctions";
import { useQuery } from "react-query";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../../Notifications/NotificationsUtils";
import { getAnnouncements } from "../../../Requests/announcement-requests";
import { Button, Pagination, Typography } from "antd";
import AnnouncementsList from "./components/lists/AnnouncementsList/AnnouncementsList";
import { usePagination } from "../../../Hooks";
import Filters from "../../Filters";
import useQueryFilters from "../../../Hooks/useQueryFilters";
import { getCurrentListUrl } from "../../../utils/navigation";

const normalizeParams = (filters: { [key: string]: string[] | string }) => {
	const searchTerm = filters?.searchTerm;
	const page = filters?.page;
	const pageSize = filters?.pageSize;
	return {
		searchTerm: Array.isArray(searchTerm)
			? searchTerm[0]
			: typeof searchTerm === "string"
			? searchTerm
			: undefined,
		page: Math.max(1, Number(Array.isArray(page) ? page[0] : page) || 1),
		pageSize:
			Math.max(1, Number(Array.isArray(pageSize) ? pageSize[0] : pageSize)) ||
			12,
	};
};

export const AnnouncementsView = (props: AnnouncementsViewProps) => {
	const { t } = useTranslation();
	const {
		currentPage,
		setCurrentPage,
		pageSize,
		setPageSize,
		handleListPaginationChange,
	} = usePagination(1, 12);
	const [searchTerm, setSearchTerm] = useState("");
	const history = useHistory();
	const query = useQueryFilters({});

	const getOldLocationValues = () => {
		if (props.location?.state) {
			const state = props.location.state as {
				searchTerm?: string;
				currentPage?: number;
				pageSize?: number;
			};
			if (state.searchTerm !== undefined) setSearchTerm(state.searchTerm);
			if (state.currentPage !== undefined) setCurrentPage(state.currentPage);
			if (state.pageSize !== undefined) setPageSize(state.pageSize);
			history.replace({ ...history.location, state: undefined });
		}
	};

	useBeforeRender(() => getOldLocationValues(), [props.location]);

	const openGetErrorNotification = (_error: unknown) => {
		openNotification(
			t("announcements.error"),
			t("announcements.fetchError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const params = normalizeParams(query.filters || {});

	const { data: announcementsData, isLoading: loadingAnnouncements } = useQuery(
		["getAnnouncements", params],
		() => getAnnouncements(params),
		{
			onError: openGetErrorNotification,
		}
	);

	const onUpdate = useCallback(
		(values: { [key: string]: string[] }) => {
			query.update(values, true);
		},
		[query]
	);

	const onPageUpdate = useCallback(
		(page: number, newPageSize: number) => {
			query.update({
				page: page.toString(),
				pageSize: newPageSize.toString(),
			});
		},
		[query]
	);

	const goToCreate = useCallback(() => {
		history.push("/adaugare-anunt", {
			returnUrl: getCurrentListUrl(),
			searchTerm,
			currentPage: params.page,
			pageSize: params.pageSize,
		});
	}, [history, searchTerm, params.page, params.pageSize]);

	return (
		<div className="px-4 py-10 flex flex-col relative">
			<div className="fixed bottom-10 md:relative md:bottom-auto md:self-end right-10 z-50">
				<Button
					type="primary"
					className="p-6 flex flex-col md:flex-row justify-center items-center rounded-full md:rounded-md relative"
					onClick={goToCreate}
				>
					{t("announcements.add")}
				</Button>
			</div>

			<Filters
				searchFields={[
					{ name: "searchTerm", label: t("announcements.search") },
				]}
				onUpdate={onUpdate}
			/>

			<div className="flex flex-col gap-10 relative">
				<div className="flex flex-col md:flex-row justify-between items-center">
					<Typography.Title level={3} className="my-0">
						{announcementsData?.totalCount ?? 0} {t("filtering.results")}
					</Typography.Title>
					{!loadingAnnouncements && (
						<Pagination
							current={params.page}
							total={announcementsData?.totalCount ?? 0}
							onChange={onPageUpdate}
							locale={{ items_per_page: t("pagination") }}
							pageSizeOptions={[12, 24, 48]}
							pageSize={params.pageSize}
						/>
					)}
				</div>

				<AnnouncementsList
					data={announcementsData}
					loading={loadingAnnouncements}
					location={props.location}
					currentPage={params.page}
					pageSize={params.pageSize}
					searchTerm={params.searchTerm}
					handleListChange={handleListPaginationChange}
				/>
			</div>
		</div>
	);
};
